import { useEffect, useRef } from 'react';

import type { PageSummary } from '../api';
import { t } from '../i18n';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { Glyph, ICON } from './icons';

interface PageActionMenuProps {
  page: PageSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onToggleFavorite: (page: PageSummary) => void;
  onOpenInNewTab: (pageId: string) => void;
  onRename: (pageId: string) => void;
  onDuplicate: (page: PageSummary) => void;
  onCopyLink: (page: PageSummary) => void;
  onTrash: (page: PageSummary) => void;
  onAddComment: () => void;
}

export function PageActionMenu({
  page,
  open,
  onOpenChange,
  onToggleFavorite,
  onOpenInNewTab,
  onRename,
  onDuplicate,
  onCopyLink,
  onTrash,
  onAddComment,
}: PageActionMenuProps): React.ReactElement {
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const button = trigger.current;
    const fallback = button?.closest('nav')?.querySelector('button');
    return () => {
      // A removed page can take the focused trigger with it after the request completes.
      queueMicrotask(() => {
        if (button && !button.isConnected && document.activeElement === document.body) {
          if (fallback?.isConnected) fallback.focus();
        }
      });
    };
  }, []);

  return (
    <ActionMenu
      open={open}
      onOpenChange={onOpenChange}
      label={t('sidebar.pageOptions')}
      className="menu--page-action"
      trigger={
        <button
          ref={trigger}
          type="button"
          className="iconbtn page-action-trigger"
          aria-label={t('sidebar.pageOptions')}
          title={t('sidebar.pageOptions')}
        >
          <Glyph d={ICON.more} size={14} />
        </button>
      }
    >
      <ActionMenuItem transfersFocus onSelect={onAddComment}>
        {t('page.menu.addComment')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={() => onToggleFavorite(page)}>
        {page.favorite ? t('page.menu.unfavorite') : t('page.menu.favorite')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={() => onOpenInNewTab(page.id)}>
        {t('page.menu.newTab')}
      </ActionMenuItem>
      <ActionMenuItem transfersFocus onSelect={() => onRename(page.id)}>
        {t('page.menu.rename')}
      </ActionMenuItem>
      <ActionMenuItem onSelect={() => onDuplicate(page)}>{t('page.menu.duplicate')}</ActionMenuItem>
      <ActionMenuItem onSelect={() => onCopyLink(page)}>{t('page.menu.copyLink')}</ActionMenuItem>
      <ActionMenuItem danger onSelect={() => onTrash(page)}>
        {t('page.menu.trash')}
      </ActionMenuItem>
    </ActionMenu>
  );
}
